export const SIMPLE_MODE_TEMPLATE_STORAGE_KEY = 'geovera_simple_mode_template';
export const LEGACY_SIMPLE_MODE_PERSONA_STORAGE_KEY = 'geovera_simple_mode_persona';
export const SIMPLE_MODE_STARTED_TASKS_STORAGE_KEY = 'geovera_simple_mode_started_tasks';

export const WORK_TEMPLATE_IDS = [
  'custom',
  'general',
  'land-surveyor',
  'infrastructure-engineer',
  'land-acquisition',
  'horticulture',
] as const;

export type WorkTemplateId = (typeof WORK_TEMPLATE_IDS)[number];

export type WorkflowTone = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

export type WorkflowIconId =
  | 'upload'
  | 'boundary'
  | 'measure'
  | 'three-d'
  | 'report'
  | 'points'
  | 'property'
  | 'share'
  | 'cloud'
  | 'survey'
  | 'design'
  | 'terrain'
  | 'field'
  | 'attributes'
  | 'crs'
  | 'analysis';

export type SpecialistActivityId = 'survey' | 'design' | 'terrain' | 'field' | 'crs' | 'analysis';

export type WorkflowTaskAction =
  | {
      kind: 'dialog';
      dialog: 'draw-boundary' | 'generate-report' | 'property-card' | 'share-map';
    }
  | { kind: 'drawing'; geometry: 'point' }
  | { kind: 'measurement'; mode: 'distance' }
  | {
      kind: 'event';
      eventName:
        | 'geovera:open-import'
        | 'geovera:toggle-3d'
        | 'geovera:open-project'
        | 'geovera:open-attributes';
      detail?: { enabled: true } | { mode: 'save' };
    }
  | { kind: 'activity'; activityId: SpecialistActivityId };

export const WORKFLOW_TASK_IDS = [
  'upload-data',
  'draw-boundary',
  'measure-area',
  'view-3d',
  'generate-report',
  'add-points',
  'property-card',
  'share-map',
  'save-cloud',
  'survey-tools',
  'design-tools',
  'terrain-tools',
  'field-tools',
  'attribute-table',
  'coordinate-system',
  'analysis-tools',
] as const;

export type WorkflowTaskId = (typeof WORKFLOW_TASK_IDS)[number];

export interface WorkflowTaskDefinition {
  id: WorkflowTaskId;
  title: string;
  description: string;
  outcomeHint: string;
  actionLabel: string;
  icon: WorkflowIconId;
  tone: WorkflowTone;
  action: WorkflowTaskAction;
}

export const WORKFLOW_TASKS = {
  'upload-data': {
    id: 'upload-data',
    title: 'Add your existing data',
    description: 'Bring a GeoJSON, Shapefile, KML, or another supported file onto the map.',
    outcomeHint: 'Your data appears as a map layer you can inspect and style.',
    actionLabel: 'Choose a file',
    icon: 'upload',
    tone: 'primary',
    action: { kind: 'event', eventName: 'geovera:open-import' },
  },
  'draw-boundary': {
    id: 'draw-boundary',
    title: 'Outline an area',
    description: 'Click around a property, site, or work area with guided steps.',
    outcomeHint: 'You get a saved outline on the live map.',
    actionLabel: 'Start drawing',
    icon: 'boundary',
    tone: 'secondary',
    action: { kind: 'dialog', dialog: 'draw-boundary' },
  },
  'measure-area': {
    id: 'measure-area',
    title: 'Measure distance or area',
    description: 'Click along a route or around an area to check its size.',
    outcomeHint: 'The result stays visible while you compare the site.',
    actionLabel: 'Start measuring',
    icon: 'measure',
    tone: 'warning',
    action: { kind: 'measurement', mode: 'distance' },
  },
  'view-3d': {
    id: 'view-3d',
    title: 'Preview the site in 3D',
    description: 'Open a movable 3D view below the map for a clearer sense of shape and height.',
    outcomeHint: 'You can rotate and zoom the 3D preview without leaving the map.',
    actionLabel: 'Open 3D',
    icon: 'three-d',
    tone: 'info',
    action: { kind: 'event', eventName: 'geovera:toggle-3d', detail: { enabled: true } },
  },
  'generate-report': {
    id: 'generate-report',
    title: 'Create a PDF summary',
    description: 'Turn the current map into a clear document for review or records.',
    outcomeHint: 'You get a downloadable PDF with your chosen map details.',
    actionLabel: 'Create report',
    icon: 'report',
    tone: 'error',
    action: { kind: 'dialog', dialog: 'generate-report' },
  },
  'add-points': {
    id: 'add-points',
    title: 'Mark important places',
    description: 'Drop pins for gates, samples, assets, issues, or landmarks.',
    outcomeHint: 'Each click adds a point you can keep with the project.',
    actionLabel: 'Add points',
    icon: 'points',
    tone: 'success',
    action: { kind: 'drawing', geometry: 'point' },
  },
  'property-card': {
    id: 'property-card',
    title: 'Make a property card',
    description: 'Find a place, mark its corners, and collect side lengths, area, DIGIPIN, and QR.',
    outcomeHint: 'You get a concise property card ready to save or share.',
    actionLabel: 'Start property card',
    icon: 'property',
    tone: 'primary',
    action: { kind: 'dialog', dialog: 'property-card' },
  },
  'share-map': {
    id: 'share-map',
    title: 'Share a view-only map',
    description: 'Create a link that others can open without changing your work.',
    outcomeHint: 'You get a copyable link for colleagues or clients.',
    actionLabel: 'Create link',
    icon: 'share',
    tone: 'secondary',
    action: { kind: 'dialog', dialog: 'share-map' },
  },
  'save-cloud': {
    id: 'save-cloud',
    title: 'Save the project',
    description: 'Keep your layers, drawings, and map setup together in your cloud account.',
    outcomeHint: 'You can reopen the same workspace later.',
    actionLabel: 'Save project',
    icon: 'cloud',
    tone: 'info',
    action: { kind: 'event', eventName: 'geovera:open-project', detail: { mode: 'save' } },
  },
  'survey-tools': {
    id: 'survey-tools',
    title: 'Check survey observations',
    description: 'Review coordinate geometry, traverse closure, control networks, and stake lists.',
    outcomeHint: 'Opens the available Survey & COGO checks in Advanced Mode.',
    actionLabel: 'Open survey panel',
    icon: 'survey',
    tone: 'primary',
    action: { kind: 'activity', activityId: 'survey' },
  },
  'design-tools': {
    id: 'design-tools',
    title: 'Plan an alignment',
    description:
      'Create horizontal alignments, stations, profiles, and preliminary earthwork estimates.',
    outcomeHint: 'Opens the available civil alignment and profile tools in Advanced Mode.',
    actionLabel: 'Open design panel',
    icon: 'design',
    tone: 'warning',
    action: { kind: 'activity', activityId: 'design' },
  },
  'terrain-tools': {
    id: 'terrain-tools',
    title: 'Inspect the ground surface',
    description:
      'Create contours, sample available terrain data, and preview mapped features in 3D.',
    outcomeHint: 'Opens the available contour, terrain sampling, and 3D tools in Advanced Mode.',
    actionLabel: 'Open terrain panel',
    icon: 'terrain',
    tone: 'success',
    action: { kind: 'activity', activityId: 'terrain' },
  },
  'field-tools': {
    id: 'field-tools',
    title: 'Prepare field collection',
    description: 'Set up mobile forms, positioning, and offline field work.',
    outcomeHint: 'Opens the Field Collection & GNSS specialist panel in Advanced Mode.',
    actionLabel: 'Open field panel',
    icon: 'field',
    tone: 'secondary',
    action: { kind: 'activity', activityId: 'field' },
  },
  'attribute-table': {
    id: 'attribute-table',
    title: 'Review the data table',
    description: 'See the information attached to features in your map layers.',
    outcomeHint: 'A table opens so you can review and compare feature details.',
    actionLabel: 'Open data table',
    icon: 'attributes',
    tone: 'info',
    action: { kind: 'event', eventName: 'geovera:open-attributes' },
  },
  'coordinate-system': {
    id: 'coordinate-system',
    title: 'Check map coordinates',
    description: 'Review or change how locations and measurements are referenced.',
    outcomeHint: 'Opens the Coordinate System specialist panel in Advanced Mode.',
    actionLabel: 'Open coordinate panel',
    icon: 'crs',
    tone: 'primary',
    action: { kind: 'activity', activityId: 'crs' },
  },
  'analysis-tools': {
    id: 'analysis-tools',
    title: 'Compare and analyze areas',
    description: 'Use buffers, overlays, proximity checks, and other advanced comparisons.',
    outcomeHint: 'Opens the Spatial Analysis specialist panel in Advanced Mode.',
    actionLabel: 'Open analysis panel',
    icon: 'analysis',
    tone: 'error',
    action: { kind: 'activity', activityId: 'analysis' },
  },
} satisfies Record<WorkflowTaskId, WorkflowTaskDefinition>;

export interface WorkTemplate {
  id: WorkTemplateId;
  title: string;
  purpose: string;
  outcome: string;
  taskIds: readonly WorkflowTaskId[];
}

export const WORK_TEMPLATES = {
  custom: {
    id: 'custom',
    title: 'Build my own',
    purpose: 'Start from the task you need instead of choosing a profession or fixed workflow.',
    outcome:
      'A flexible workspace where you can use any mapping, field, analysis, or reporting tool.',
    taskIds: WORKFLOW_TASK_IDS,
  },
  general: {
    id: 'general',
    title: 'Common map tasks',
    purpose: 'Explore a place, add information, and make a map you can explain to others.',
    outcome: 'A clear map with useful measurements, notes, and a shareable result.',
    taskIds: [
      'upload-data',
      'draw-boundary',
      'measure-area',
      'add-points',
      'view-3d',
      'generate-report',
      'share-map',
      'save-cloud',
    ],
  },
  'land-surveyor': {
    id: 'land-surveyor',
    title: 'Land Surveyor',
    purpose: 'Prepare boundary, control, coordinate, measurement, and survey review work.',
    outcome: 'A survey-ready workspace with checked coordinates, mapped observations, and outputs.',
    taskIds: [
      'upload-data',
      'coordinate-system',
      'survey-tools',
      'draw-boundary',
      'measure-area',
      'add-points',
      'generate-report',
      'save-cloud',
    ],
  },
  'infrastructure-engineer': {
    id: 'infrastructure-engineer',
    title: 'Infrastructure Engineer',
    purpose: 'Understand a site, test options, and prepare road or civil design work.',
    outcome: 'A project view that connects terrain, design checks, analysis, and review outputs.',
    taskIds: [
      'upload-data',
      'terrain-tools',
      'design-tools',
      'analysis-tools',
      'measure-area',
      'add-points',
      'view-3d',
      'generate-report',
      'share-map',
    ],
  },
  'land-acquisition': {
    id: 'land-acquisition',
    title: 'Land Acquisition',
    purpose: 'Review parcels, compare site facts, and prepare clear records for decisions.',
    outcome: 'A traceable property view with dimensions, attached details, and shareable evidence.',
    taskIds: [
      'property-card',
      'upload-data',
      'draw-boundary',
      'measure-area',
      'attribute-table',
      'generate-report',
      'share-map',
      'save-cloud',
    ],
  },
  horticulture: {
    id: 'horticulture',
    title: 'Horticulture',
    purpose: 'Map growing areas, mark field observations, and understand access and terrain.',
    outcome: 'A practical field map of blocks, samples, issues, measurements, and site conditions.',
    taskIds: [
      'draw-boundary',
      'add-points',
      'measure-area',
      'field-tools',
      'terrain-tools',
      'view-3d',
      'generate-report',
      'share-map',
    ],
  },
} satisfies Record<WorkTemplateId, WorkTemplate>;

export const isWorkTemplateId = (value: string | null): value is WorkTemplateId =>
  value !== null && WORK_TEMPLATE_IDS.some((id) => id === value);
